import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, MenuItem, Button, Stack, Typography, Alert, LinearProgress,
} from '@mui/material';
import axiosInstance from '../../utils/axios';
import { AdminPageHeader, useAdminFeedback } from '../../components/admin/primitives';

export default function AdminBroadcasts() {
  const { showSuccess, showError, Feedback } = useAdminFeedback();
  const [audience, setAudience] = useState('all');
  const [divisionId, setDivisionId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [divisions, setDivisions] = useState([]);

  React.useEffect(() => {
    axiosInstance.get('/divisions').then(({ data }) => {
      const items = Array.isArray(data) ? data : (data?.items || data?.divisions || []);
      setDivisions(items);
    }).catch(() => setDivisions([]));
  }, []);

  const send = async () => {
    if (!title.trim() || !message.trim()) {
      showError('Title and message are required');
      return;
    }
    if (audience === 'division' && !divisionId) {
      showError('Pick a division');
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post('/admin/broadcasts', {
        audience,
        divisionId: audience === 'division' ? divisionId : undefined,
        title: title.trim(),
        message: message.trim(),
      });
      showSuccess('Broadcast sent');
      setTitle('');
      setMessage('');
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to send broadcast');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, py: 3 }}>
      <AdminPageHeader
        title="Broadcasts"
        description="Send an announcement to Discord (and optionally set the in-app division banner for a division)."
      />
      {busy && <LinearProgress sx={{ mb: 2 }} />}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="info">
              Messages go to the configured Discord webhook. Division audience also updates that division&apos;s member announcement banner when supported.
            </Alert>
            <TextField
              select
              label="Audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              fullWidth
            >
              <MenuItem value="all">All riders (Discord)</MenuItem>
              <MenuItem value="division">One division (Discord + banner)</MenuItem>
            </TextField>
            {audience === 'division' && (
              <TextField
                select
                label="Division"
                value={divisionId}
                onChange={(e) => setDivisionId(e.target.value)}
                fullWidth
              >
                {divisions.map((d) => (
                  <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
            <Button variant="contained" onClick={send} disabled={busy}>
              Send broadcast
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Feedback />
    </Box>
  );
}
